import React, { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { REQUEST_STATUS, ROUTES } from '../../core/actions'
import { useAxios } from '../../core/AxiosContext'
import { useGlobal, useGlobalDispatch } from '../../core/GlobalContext'
import {
  createFinancierBankAccountAction,
  createVerifiedBankAccountAction,
  fetchEarningsReportUrlAction,
  fetchMisReportUrlAction,
  getNomineeDetailsAction,
  referralDetailsAction,
  updateFinancierBankAccountAction,
} from '../../routes/financier/FinancierDuck'
import { camelToWords } from '../../utils/misc'
import { AddUpiAttributes } from '../addUpiAttributes/AddUpiAttributes'
import Button from '../button'
import CardAttributes from '../cardAttributes'
import ChangeBankAccount from '../changeBankAccount'
import NomineeDetails from '../nomineeDetails'
import PasswordReset from '../passwordReset'
import Text from '../text'
import { VerifiedDepositAccounts } from '../verifiedDepositAccounts/VerifiedDepositAccounts'
import * as styles from './ProfileDetails.css'

const NAV_SECTIONS = [
  { key: 'identity', label: 'Identity', icon: '👤' },
  { key: 'kyc', label: 'KYC & verification', icon: '🛡️' },
  { key: 'bank', label: 'Bank & payouts', icon: '🏦' },
  { key: 'nominee', label: 'Nominees', icon: '👥' },
  { key: 'security', label: 'Security', icon: '🔒' },
  { key: 'referral', label: 'Refer & earn', icon: '🎁' },
  { key: 'reports', label: 'Reports & FAQ', icon: '📄' },
]

const initialsOf = name =>
  (name || '')
    .trim()
    .split(/\s+/)
    .map(word => word[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase() || '?'

const openInNewTab = link => {
  if (!link) return
  const a = document.createElement('a')
  a.href = link
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
}

export const ProfileDetails = ({ financier, logout, handleCopyReferral }) => {
  const { authAxios } = useAxios()
  const {
    financierState: { nominees, referrals, misReportStatus, earningsReportStatus },
  } = useGlobal()
  const { financierDispatch } = useGlobalDispatch()

  const [activeSection, setActiveSection] = useState('identity')
  const [showEditBank, setShowEditBank] = useState(false)
  const [showAddBank, setShowAddBank] = useState(false)
  const [selectedDepositAcnt, setSelectedDepositAcnt] = useState('bank')
  const [addNominee, setAddNominee] = useState(false)
  const [showResetPassword, setShowResetPassword] = useState(false)

  const navigate = useNavigate()

  const authorizedUser = financier?.authorizedUser || {}

  const viewAuthorizedUser = ['fullName', 'PAN', 'aadhaarNumber', 'emailId', 'phoneNumber']
  const viewEscrow = ['accountNumber', 'holderName', 'bankName', 'IFSC', 'branch', 'branchAddress']
  const viewFinancierBank = ['accountNumber', 'holderName', 'bankName', 'IFSC']

  // Pull nominees + referrals up-front so KYC completeness and referral counts are accurate
  useEffect(() => {
    if (financier?.financierPAN) {
      getNomineeDetailsAction(financier.financierPAN)(authAxios, financierDispatch)
      referralDetailsAction(financier.financierPAN)(authAxios, financierDispatch)
    }
  }, [financier?.financierPAN])

  // ── Derived: KYC completeness (computed from data we actually have, no new API) ──
  const kycChecks = [
    { label: 'PAN verified', ok: !!authorizedUser.PAN, detail: authorizedUser.PAN || 'Pending' },
    {
      label: 'Aadhaar / identity verified',
      ok: !!authorizedUser.aadhaarNumber,
      detail: authorizedUser.aadhaarNumber || 'Pending',
    },
    {
      label: 'Bank account linked',
      ok: !!financier?.financierBank?.id,
      detail: financier?.financierBank?.bankName || 'Not linked',
    },
    { label: 'Email verified', ok: !!authorizedUser.emailId, detail: authorizedUser.emailId || 'Not added' },
    {
      label: 'Nominee added',
      ok: nominees?.length > 0,
      detail: nominees?.length > 0 ? `${nominees.length} added` : 'None yet',
    },
  ]
  const kycDone = kycChecks.filter(check => check.ok).length
  const kycPct = Math.round((kycDone / kycChecks.length) * 100)
  const kycVerified = !!authorizedUser.PAN && !!authorizedUser.aadhaarNumber

  const handleReport = action =>
    action(financier?.financierPAN)(authAxios, financierDispatch)(url => openInNewTab(url))

  const SectionHeader = ({ title, action }) => (
    <div className={styles.dhead}>
      <h2 className={styles.dheadTitle}>{title}</h2>
      {action}
    </div>
  )

  return (
    <div className={styles.layout}>
      {/* ─────────── LEFT: profile card + section nav ─────────── */}
      <div>
        <div className={`${styles.panel} ${styles.pcard}`}>
          <div className={styles.avatar}>{initialsOf(financier?.financierName)}</div>
          <div className={styles.pcardName}>{financier?.financierName}</div>
          <div className={styles.pcardPan}>{financier?.financierPAN}</div>
          {kycVerified && <div className={styles.kycBadge}>✓ KYC Verified</div>}
        </div>

        <div className={`${styles.panel} ${styles.snav}`}>
          {NAV_SECTIONS.map(section => {
            const isActive = activeSection === section.key
            let tag = null
            if (section.key === 'kyc') {
              tag = (
                <span className={`${styles.tag} ${kycPct < 100 ? styles.tagWarn : ''}`}>
                  {kycPct === 100 ? 'Done' : `${kycPct}%`}
                </span>
              )
            } else if (section.key === 'nominee') {
              tag = (
                <span className={`${styles.tag} ${nominees?.length ? '' : styles.tagWarn}`}>{nominees?.length || 0}</span>
              )
            }
            return (
              <button
                key={section.key}
                className={`${styles.snavBtn} ${isActive ? styles.snavBtnActive : ''}`}
                onClick={() => setActiveSection(section.key)}>
                <span className={styles.snavIc}>{section.icon}</span> {section.label}
                {tag}
              </button>
            )
          })}
          <button className={`${styles.snavBtn} ${styles.logout}`} onClick={() => logout && logout()}>
            <span className={styles.snavIc}>⏻</span> Log out
          </button>
        </div>
      </div>

      {/* ─────────── RIGHT: active section detail ─────────── */}
      <div className={`${styles.panel} ${styles.detail}`}>
        {/* Identity */}
        {activeSection === 'identity' && (
          <div>
            <SectionHeader title='Identity' />
            <div className={styles.dbody}>
              <div className={styles.rows}>
                {Object.entries(authorizedUser)
                  .filter(entry => viewAuthorizedUser.includes(entry[0]))
                  .map(entry => (
                    <CardAttributes
                      key={entry[0]}
                      name={camelToWords(entry[0])}
                      value={entry[1]}
                      userId={authorizedUser?.id}
                    />
                  ))}
              </div>
            </div>
          </div>
        )}

        {/* KYC & verification */}
        {activeSection === 'kyc' && (
          <div>
            <SectionHeader title='KYC & verification' />
            <div className={styles.dbody}>
              <div className={styles.completeness}>
                <div className={styles.ring} style={{ '--p': kycPct }}>
                  <b className={styles.ringInner}>{kycPct}%</b>
                </div>
                <div>
                  <b className={styles.complTitle}>
                    {kycPct === 100 ? 'Your account is fully verified' : 'A few steps left to complete'}
                  </b>
                  <p className={styles.complSub}>
                    {kycPct === 100
                      ? "You're all set to invest. Everything below is confirmed."
                      : 'Complete the pending items below to unlock everything.'}
                  </p>
                </div>
              </div>
              <div className={styles.checks}>
                {kycChecks.map(check => (
                  <div key={check.label} className={styles.check}>
                    <span className={`${styles.dot} ${check.ok ? styles.dotOk : styles.dotNo}`}>
                      {check.ok ? '✓' : '!'}
                    </span>
                    {check.label}
                    <span className={styles.checkSt}>{check.detail}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Bank & payouts (escrow + withdrawal bank + verified deposit sources) */}
        {activeSection === 'bank' && (
          <div>
            <SectionHeader title='Bank & payouts' />
            <div className={styles.dbody}>
              <div className={styles.sectionLabel}>Income Bazaar wallet (escrow)</div>
              <div className={styles.acct}>
                {Object.entries(financier?.escrow || {})
                  .filter(entry => viewEscrow.includes(entry[0]))
                  .map(entry => (
                    <CardAttributes key={entry[0]} name={camelToWords(entry[0])} value={entry[1]} />
                  ))}
              </div>

              <div className={styles.sectionHead}>
                <div className={styles.sectionLabel}>Withdrawal bank account</div>
                <Button type='text' handleClick={() => setShowEditBank(val => !val)}>
                  {showEditBank ? 'Cancel' : financier?.financierBank?.id ? 'Edit' : 'Add account'}
                </Button>
              </div>
              <div className={styles.acct}>
                {showEditBank ? (
                  <ChangeBankAccount
                    handleOnSubmit={
                      financier?.financierBank?.id ? updateFinancierBankAccountAction : createFinancierBankAccountAction
                    }
                    handleClose={() => setShowEditBank(false)}
                    title={financier?.financierBank?.id ? 'Edit bank Account' : 'Add bank Account'}
                  />
                ) : financier?.financierBank?.id ? (
                  Object.entries(financier?.financierBank)
                    .filter(entry => viewFinancierBank.includes(entry[0]))
                    .map(entry => <CardAttributes key={entry[0]} name={camelToWords(entry[0])} value={entry[1]} />)
                ) : (
                  <Text color='var(--ib-muted)'>Add your bank account to process withdrawals from wallet</Text>
                )}
              </div>

              <div className={styles.sectionHead}>
                <div className={styles.sectionLabel}>Verified deposit sources</div>
                <Button type='text' handleClick={() => setShowAddBank(val => !val)}>
                  {showAddBank ? 'Cancel' : 'Add account'}
                </Button>
              </div>
              <Text size='0.8' color='var(--ib-muted)'>
                Credits to Income Bazaar will reflect only if done from these verified accounts
              </Text>
              {showAddBank ? (
                <div className={styles.acct}>
                  <div className={styles.addrow}>
                    <Button
                      label='Bank Account'
                      className={selectedDepositAcnt === 'bank' ? styles.selectedDepositAcnt : ''}
                      type='skeleton'
                      handleClick={() => setSelectedDepositAcnt('bank')}
                    />
                    <Button
                      label='UPI'
                      className={selectedDepositAcnt === 'upi' ? styles.selectedDepositAcnt : ''}
                      type='skeleton'
                      handleClick={() => setSelectedDepositAcnt('upi')}
                    />
                  </div>
                  {selectedDepositAcnt === 'bank' ? (
                    <ChangeBankAccount
                      handleOnSubmit={createVerifiedBankAccountAction}
                      handleClose={() => setShowAddBank(false)}
                      title={'Add verified account'}
                    />
                  ) : (
                    <AddUpiAttributes handleClose={() => setShowAddBank(false)} />
                  )}
                </div>
              ) : (
                <VerifiedDepositAccounts />
              )}
            </div>
          </div>
        )}

        {/* Nominees */}
        {activeSection === 'nominee' && (
          <div>
            <SectionHeader
              title='Nominees'
              action={
                <Button className={styles.editBtn} type='text' handleClick={() => setAddNominee(val => !val)}>
                  {addNominee ? 'Cancel' : '+ Add nominee'}
                </Button>
              }
            />
            <div className={styles.dbody}>
              <NomineeDetails addNominee={addNominee} setAddNominee={setAddNominee} />
              {!addNominee && (
                <p className={styles.hint}>You can add up to 3 nominees and split the allocation between them.</p>
              )}
            </div>
          </div>
        )}

        {/* Security */}
        {activeSection === 'security' && (
          <div>
            <SectionHeader title='Security' />
            <div className={styles.dbody}>
              {showResetPassword ? (
                <PasswordReset
                  pan={financier?.financierPAN}
                  phone={authorizedUser?.phone}
                  showResetPassword={showResetPassword}
                  setShowResetPassword={setShowResetPassword}
                />
              ) : (
                <div>
                  <Text color='var(--ib-muted)'>
                    Change your account password. A one-time password will be sent to the mobile number linked to your
                    PAN to confirm the change.
                  </Text>
                  <div className={styles.addrow}>
                    <Button className={styles.btnRed} label='Change password' handleClick={() => setShowResetPassword(true)} />
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Refer & earn */}
        {activeSection === 'referral' && (
          <div>
            <SectionHeader title='Refer & earn' />
            <div className={styles.dbody}>
              <div className={styles.refcard}>
                <div className={styles.refLbl}>Your referral code</div>
                <div className={styles.refcode}>
                  <b className={styles.refcodeVal}>{financier?.referralCode || '—'}</b>
                  <button className={styles.refcodeBtn} onClick={() => handleCopyReferral && handleCopyReferral()}>
                    Copy link
                  </button>
                </div>
                <div className={styles.refstats}>
                  <div className={styles.refstat}>
                    <b className={styles.tnum}>{referrals?.length || 0}</b>
                    <span>Friends joined</span>
                  </div>
                </div>
              </div>
              <p className={styles.hint}>
                Share your code — you both earn when a friend makes their first investment.
              </p>
            </div>
          </div>
        )}

        {/* Reports & FAQ */}
        {activeSection === 'reports' && (
          <div>
            <SectionHeader title='Reports & FAQ' />
            <div className={styles.dbody}>
              <div className={styles.reports}>
                <div
                  className={styles.rtile}
                  onClick={() => misReportStatus !== REQUEST_STATUS.PENDING && handleReport(fetchMisReportUrlAction)}>
                  <span className={styles.rtileIc}>📊</span>
                  <div>
                    <b>MIS report</b>
                    <p>{misReportStatus === REQUEST_STATUS.PENDING ? 'Generating…' : 'Full transaction statement'}</p>
                  </div>
                </div>
                <div
                  className={styles.rtile}
                  onClick={() =>
                    earningsReportStatus !== REQUEST_STATUS.PENDING && handleReport(fetchEarningsReportUrlAction)
                  }>
                  <span className={styles.rtileIc}>💰</span>
                  <div>
                    <b>Earnings statement</b>
                    <p>{earningsReportStatus === REQUEST_STATUS.PENDING ? 'Generating…' : 'Realized income & TDS'}</p>
                  </div>
                </div>
                <div className={styles.rtile} onClick={() => navigate(ROUTES.REPORTS)}>
                  <span className={styles.rtileIc}>📈</span>
                  <div>
                    <b>Outstanding report</b>
                    <p>Active positions & maturity</p>
                  </div>
                </div>
                <Link className={styles.rtile} to={ROUTES.FAQ}>
                  <span className={styles.rtileIc}>❓</span>
                  <div>
                    <b>FAQ & help</b>
                    <p>Common questions</p>
                  </div>
                </Link>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
